import React, { useEffect, useState } from "react";

import { LogOut, Settings, Wrench } from "lucide-react";

import { trackEvent } from "@needl-ai/common/analytics/analytics";
import { signOut } from "@needl-ai/common/auth/auth";
import {
  useIsNeedlBranding,
  useIsSoftwareOneBranding,
} from "@needl-ai/common/branding/hooks";
import {
  Popover,
  PopoverContent,
} from "@needl-ai/common/components/ui/popover";
import { useTheme } from "@needl-ai/common/providers/ThemeContext";
import { isMSquaredHub } from "@needl-ai/common/utils/isEnv";
import { getRequestOrigin } from "@needl-ai/common/utils/requestOrigin";
import { storeReturnUrl } from "@needl-ai/common/utils/returnToApp";
import { useGetPathPrefix } from "@needl-ai/common/workspace";

type UserProfilePopupProps = {
  name: string;
  email?: string;
  children: React.ReactNode;
};

export const UserProfilePopup = ({
  name,
  email,
  children,
}: UserProfilePopupProps) => {
  const { setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const isNeedl = useIsNeedlBranding();
  const isSoftwareOne = useIsSoftwareOneBranding();
  const pathPrefix = useGetPathPrefix();
  const isMsquaredHubEnv = isMSquaredHub();

  const isOnSettingsPage =
    typeof window !== "undefined" &&
    window.location.pathname.includes("/settings");

  const isOnManagePage =
    typeof window !== "undefined" &&
    window.location.pathname.includes("/manager");

  // Hopping between Settings and Manage inside console must not overwrite the
  // app URL that got the user here.
  const isInsideConsole = isOnSettingsPage || isOnManagePage;

  const showLogout = !isMsquaredHubEnv;
  const showSettings = !isOnSettingsPage && !isSoftwareOne;
  const showManage = !isOnManagePage && !isSoftwareOne && !isMsquaredHubEnv;

  useEffect(() => {
    if (!isNeedl) {
      setTheme("light");
    }
  }, [isNeedl, setTheme]);

  const handleOpenSettings = () => {
    setIsOpen(false);
    trackEvent("settings_option_clicked", { source: getRequestOrigin() });
    if (isMsquaredHubEnv) {
      const base = (import.meta.env.BASE_URL ?? "").replace(/\/$/, "");
      window.location.href = `${base}${pathPrefix}/settings`;
      return;
    }
    if (!isInsideConsole) storeReturnUrl();
    window.location.href = "/console/settings";
  };

  const handleOpenManage = () => {
    setIsOpen(false);
    trackEvent("manage_option_clicked", { source: getRequestOrigin() });
    if (!isInsideConsole) storeReturnUrl();
    window.location.href = "/console/manager";
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
    } finally {
      setIsLoggingOut(false);
    }
  };

  const getInitials = (fullName: string) => {
    return fullName
      .split(" ")
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase();
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      {children}
      <PopoverContent
        className="w-64 p-1 shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.1),0px_4px_6px_-1px_rgba(0,0,0,0.1)] bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700"
        side="right"
        align="end"
        sideOffset={8}
        style={{
          maxWidth: "16rem",
        }}
      >
        <div className="flex flex-col">
          <div className="flex items-center gap-2 px-2 py-1.5 min-h-8 bg-white dark:bg-neutral-900 rounded-sm">
            <div className="w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm bg-primary text-primary-foreground">
              {getInitials(name)}
            </div>
            <div className="flex flex-col min-w-0">
              <div className="text-sm font-normal text-zinc-900 dark:text-white leading-[22px] truncate">
                {name}
              </div>
              {email && (
                <div className="text-xs font-normal text-zinc-500 dark:text-neutral-400 leading-[22px] truncate">
                  {email}
                </div>
              )}
            </div>
          </div>

          {showSettings && (
            <div className="flex items-center justify-center px-0 py-1">
              <div className="h-px w-full bg-zinc-200 dark:bg-neutral-700"></div>
            </div>
          )}

          {showSettings && (
            <button
              type="button"
              className="flex items-center gap-2 min-h-8 px-2 py-1.5 bg-white dark:bg-neutral-900 rounded-sm hover:bg-gray-50 dark:hover:bg-neutral-800 cursor-pointer transition-colors w-full text-left focus:outline-none"
              onClick={handleOpenSettings}
              data-testid="settings-button"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <Settings className="w-4 h-4 text-zinc-900 dark:text-white" />
              </div>
              <span className="text-base font-normal text-zinc-900 dark:text-white leading-[22px]">
                Settings
              </span>
            </button>
          )}

          {showManage && (
            <button
              type="button"
              className="flex items-center gap-2 min-h-8 px-2 py-1.5 bg-white dark:bg-neutral-900 rounded-sm hover:bg-gray-50 dark:hover:bg-neutral-800 cursor-pointer transition-colors w-full text-left focus:outline-none"
              onClick={handleOpenManage}
              data-testid="manage-button"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <Wrench className="w-4 h-4 text-zinc-900 dark:text-white" />
              </div>
              <span className="text-base font-normal text-zinc-900 dark:text-white leading-[22px]">
                Manage
              </span>
            </button>
          )}

          {showLogout && (
            <button
              type="button"
              className={`flex items-center gap-2 min-h-8 px-2 py-1.5 bg-white dark:bg-neutral-900 rounded-sm hover:bg-gray-50 dark:hover:bg-neutral-800 cursor-pointer transition-colors w-full text-left focus:outline-none ${
                isLoggingOut ? "opacity-50 pointer-events-none" : ""
              }`}
              onClick={handleLogout}
              disabled={isLoggingOut}
              data-testid="logout-button"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <LogOut className="w-4 h-4 text-zinc-900 dark:text-white" />
              </div>
              <span className="text-base font-normal text-zinc-900 dark:text-white leading-[22px]">
                {isLoggingOut ? "Logging out..." : "Log out"}
              </span>
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
